// Ch97 · PKT-0792 PR 2 · the deep links ContentPage honours, carried to Socials.
//
// 🔴 WHY THIS EXISTS. PKT-0792 Phase 1 says, verbatim: "Rename nav + page to
// Socials (keep `?page=content` and `?tab=` working)". Measured at 9c36194:
// ContentPage honours four URL shapes and SocialsPage honours NONE — the strings
// `searchParams`, `URLSearchParams` and `location.search` appear zero times in it.
// Mounting the new tab without this layer breaks every one of them SILENTLY: no
// error, just the wrong tab, and `?lead=` stops resolving to its conversation.
//
// WHAT CONTENTPAGE ACTUALLY DOES (ContentPage.tsx:236-243, :711) — read from the
// code, not from the packet's description of it:
//     ?tab=reddit     -> the Reddit desk
//     ?tab=instagram  -> the Instagram desk
//     ?tab=<anything else, or absent> -> the video review grid
//     ?lead=<id>      -> RedditAnswerDesk initialLead, i.e. ONE conversation
//
// This module is pure: it maps a query string to a destination and nothing else,
// so every shape is testable without a DOM or a router.

export type SocialsTabKey = "overview" | "reels" | "engage";

export interface SocialsDestination {
  tab: SocialsTabKey;
  /** The one conversation to open, from `?lead=`. null when absent. */
  lead: string | null;
  /**
   * True when the URL named a destination we recognised and honoured. False
   * when nothing in the URL applied and we fell through to the default — the
   * caller uses this to tell "he asked for Overview" from "he asked for
   * nothing", which are different facts even though they render the same.
   */
  fromLink: boolean;
}

/**
 * 🔴 THE ONE PLACE THIS DELIBERATELY DOES NOT PRESERVE CONTENTPAGE, AND WHY.
 *
 * ContentPage sends an ABSENT `?tab=` to the video grid, because the grid is its
 * only real surface. The approved mock makes Overview the first tab, and Grant
 * approved that. So:
 *
 *     ?tab=videos (EXPLICIT)  -> reels     the link ASKED for the grid; honour it
 *     absent                  -> overview  the link asked for nothing; use the
 *                                          design's own default
 *     ?tab=<unrecognised>     -> reels     ContentPage's fallback, preserved: a
 *                                          link that said SOMETHING we no longer
 *                                          recognise still meant the grid
 *
 * The distinction is between a link that named a place and a link that did not.
 * Flagged to the Director as the single intentional divergence rather than
 * buried, because "keep ?tab= working" could be read either way and a silent
 * reading of a spec is how the last Phase 1 tick went wrong.
 */
export function socialsDestination(search: string): SocialsDestination {
  const params = new URLSearchParams(search || "");
  const rawTab = params.get("tab");
  const rawLead = params.get("lead");
  const lead = rawLead && rawLead.trim() ? rawLead.trim() : null;

  // `?lead=` names a conversation, and a conversation lives in Engage. It wins
  // over the tab: a link to one thing should land on that thing.
  if (lead) return { tab: "engage", lead, fromLink: true };

  switch ((rawTab ?? "").trim().toLowerCase()) {
    case "reddit":
    case "instagram":
    case "engage":
      return { tab: "engage", lead: null, fromLink: true };
    case "videos":
    case "reels":
      return { tab: "reels", lead: null, fromLink: true };
    case "overview":
      return { tab: "overview", lead: null, fromLink: true };
    case "":
      return { tab: "overview", lead: null, fromLink: false };
    default:
      // Unrecognised but present: ContentPage's fallback was the grid.
      return { tab: "reels", lead: null, fromLink: true };
  }
}
